import { ENV_VALUES } from '@/config/env';
import { FetchJsonResponseParser } from '@/helpers/parseFetchJsonResponse';
import { GetFormMetadataResponse } from '@informme/shared/schemas/orgolas/get-form-metadata.schema';
import { GetFormResponse } from '@informme/shared/schemas/orgolas/get-form.schema';
import { GetPartialOrgResponse } from '@informme/shared/schemas/orgolas/get-partial-org.schema';
import { TEST_ORGANIZATION } from '../test-data/TestOrgData';
import { TEST_ORG_ID } from '../test-data/TestQRData';

class OrgolasRepo {
  baseUrl = ENV_VALUES.VUE_APP_ORGOLAS_API_URL;

  public async getOrg(orgId: string, sessionId: string): Promise<GetPartialOrgResponse> {
    if (orgId === TEST_ORG_ID) {
      console.log('Using test organization data for org ID:', orgId);
      return Promise.resolve(TEST_ORGANIZATION);
    }

    const res = await fetch(`${this.baseUrl}/api/v2/patient/org`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: sessionId,
      },
    });
    return FetchJsonResponseParser.parse<GetPartialOrgResponse>(res);
  }

  public fetchAvailableForms(
    sessionId: string,
    siteId?: string,
  ): {
    orgForms: Promise<GetFormMetadataResponse>;
    medudocForms: Promise<GetFormMetadataResponse>;
    withMedudoc: Promise<GetFormMetadataResponse>;
  } {
    const formsUrl = siteId
      ? `${this.baseUrl}/api/v2/patient/forms-metadata?siteId=${siteId}`
      : `${this.baseUrl}/api/v2/patient/forms-metadata`;
    const medudocUrl = `${this.baseUrl}/api/v2/patient/medudoc-procedures`;

    const orgFormsPromise: Promise<GetFormMetadataResponse> = fetch(formsUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: sessionId },
    })
      .then((res) => FetchJsonResponseParser.parse<GetFormMetadataResponse>(res))
      .then((data) => (Array.isArray(data) ? data : []))
      .catch(() => []);

    const medudocPromise: Promise<GetFormMetadataResponse> = fetch(medudocUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: sessionId },
    })
      .then((res) => FetchJsonResponseParser.parse<GetFormMetadataResponse>(res))
      .then((data) => (Array.isArray(data) ? data : []))
      .catch(() => []);

    const withMedudoc = Promise.all([orgFormsPromise, medudocPromise]).then(([forms, medudoc]) => [
      ...forms,
      ...medudoc,
    ]);

    return { orgForms: orgFormsPromise, medudocForms: medudocPromise, withMedudoc };
  }

  public async fetchForm(
    sessionId: string,
    formId: string,
    formLang: string,
  ): Promise<GetFormResponse> {
    const url = `${this.baseUrl}/api/v2/patient/form/${formId}?formLang=${formLang}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: sessionId,
      },
    });
    return FetchJsonResponseParser.parse<GetFormResponse>(res);
  }

  public async fetchMedudocProcedureForm(
    sessionId: string,
    procedureId: string,
    lang = 'en',
  ): Promise<GetFormResponse> {
    const url = `${this.baseUrl}/api/v2/patient/medudoc-procedure-form/${procedureId}?lang=${lang}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: sessionId,
      },
    });
    return FetchJsonResponseParser.parse<GetFormResponse>(res);
  }
}

export const orgolasRepo = new OrgolasRepo();
